import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import { PADX, SECTION_TITLE_TYPE } from "../constants";
import { type BrandDataset } from "../types";

// Warm cream fill for the full-bleed About block at the page's base.
// The brand secondary surface (falls back to a warm cream if a brand hasn't set one).
const ABOUT_BG = "var(--brand-secondary, rgb(253, 251, 245))";

/**
 * About module — a full-bleed cream block that caps the bottom of the page
 * above the footer. The "About {brand}" heading spans the top; below it the
 * description + tags (left two columns) and the Information list (right column)
 * share the same top baseline.
 */
interface AboutModuleProps {
  brand: BrandDataset;
  /**
   * Where the Instagram/website pair sits. "header" (default) puts it top-right
   * opposite the avatar, which is where it has always been. "belowTags" drops
   * it under the brand tags in the left column, so the block reads top to
   * bottom as one column — who they are, what they stand for, where to find
   * them — instead of opening with an outbound link.
   */
  socialPlacement?: "header" | "belowTags";
  /** Hide the "Learn more about brand tags" link under the tag row. */
  hideBrandTagsLink?: boolean;
}

export const AboutModule: React.FC<AboutModuleProps> = ({
  brand,
  socialPlacement = "header",
  hideBrandTagsLink = false,
}) => (
  <div
    // last:-mb-12 cancels RetailerLayout's 48px content padding-bottom so the
    // cream butts directly against the footer divider. Only when this block
    // really is the last thing on the page: on brand.com the social band and
    // the brand's own footer follow, and an unconditional -mb-12 pulled them
    // up over the tags row ("Learn more about brand tags" was clipped).
    className="w-full last:-mb-12"
    style={{
      backgroundColor: brand.aboutBackgroundColor ?? ABOUT_BG,
      animation: "pfHomeFade 350ms ease-out both",
    }}
  >
    <div className={cn("py-4 md:py-10 lg:py-[48px] 2xl:py-[80px]", PADX)}>
      <div className="mb-6 flex items-start justify-between">
        <div className="h-20 w-20 overflow-hidden rounded-full border border-(--slate-color-grey-1000)/10">
          <img
            src={brand.avatarUrl}
            alt={brand.name}
            className="h-full w-full object-cover"
          />
        </div>
        {socialPlacement === "header" ? <SocialLinks brand={brand} /> : null}
      </div>
      <div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-3">
        <h2
          className={cn(
            SECTION_TITLE_TYPE,
            "text-fs-text-primary md:col-span-3"
          )}
        >
          About {brand.name}
        </h2>

        {/* Left: description + tags */}
        <div className="flex flex-col gap-6 md:col-span-2">
          <p className="type-fs-paragraph text-fs-text-primary max-w-2xl">
            {brand.aboutText}
          </p>

          {brand.brandValueTags && brand.brandValueTags.length > 0 ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                {brand.brandValueTags.map((tag) => (
                  <span
                    key={tag}
                    className="type-fs-paragraph text-fs-text-primary rounded-[4px] bg-(--slate-color-grey-1000)/[0.05] px-3 py-1.5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {hideBrandTagsLink ? null : (
                <button className="type-fs-label text-fs-text-primary self-start underline">
                  Learn more about brand tags
                </button>
              )}
            </div>
          ) : null}

          {socialPlacement === "belowTags" ? (
            <SocialLinks brand={brand} />
          ) : null}
        </div>

        {/* Right: Information (no title) */}
        {brand.information && brand.information.length > 0 ? (
          <dl
            className={cn(
              "divide-y divide-(--slate-color-grey-1000)/10",
              // Stacked, this list follows the social links directly, and the
              // grid's own 16px row gap is too tight to separate a row of
              // facts from the link above it. 24px here lands the pair at 40.
              // Cancelled at `md`, where the list is a sibling COLUMN sharing
              // its top baseline with the description (Ryan, 2026-09-21).
              "mt-6 md:mt-0"
            )}
          >
            {brand.information.map((row, index) => (
              <div
                key={row.label}
                className={cn(
                  "flex items-center gap-1 py-3",
                  index === 0 && "pt-0"
                )}
              >
                <dt className="type-fs-paragraph text-fs-text-primary">
                  {row.label}
                </dt>
                <dd className="type-fs-paragraph text-fs-text-primary">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </div>
  </div>
);

/** The brand's Instagram handle and website, as one inline pair. */
const SocialLinks: React.FC<{ brand: BrandDataset }> = ({ brand }) => {
  // Most brands give us a handle and no website, so the pair has to read
  // correctly with either half missing: the separator belongs to the join, not
  // to the website, and a brand with neither renders nothing rather than an
  // empty flex row that still eats a gap.
  if (!brand.instagram && !brand.website) return null;
  return (
    <div className="text-fs-text-primary type-fs-paragraph flex items-center gap-2">
      {brand.instagram ? (
        <a
          href={`https://instagram.com/${brand.instagram.replace("@", "")}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${brand.name} on Instagram`}
          className="flex items-center gap-1.5"
        >
          <InstagramIcon />
          <span className="underline">{brand.instagram}</span>
        </a>
      ) : null}
      {brand.instagram && brand.website ? (
        <span aria-hidden className="opacity-40">
          ·
        </span>
      ) : null}
      {brand.website ? (
        <a
          href={`https://${brand.website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {brand.website}
        </a>
      ) : null}
    </div>
  );
};

const InstagramIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17" cy="7" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);
